import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, X } from 'lucide-react';

/**
 * One-time privacy notice shown on every page load until dismissed.
 *
 * Why a notice, not a consent banner:
 *   The site uses no third-party tracking cookies. Plausible Analytics
 *   is cookie-free and explicitly does not need consent under TDDDG /
 *   ePrivacy. Supabase Auth + Stripe Checkout use technically-necessary
 *   cookies only (login session + payment flow), which are also
 *   consent-exempt. So a full "Allow / Reject" banner would be both
 *   legally unnecessary and annoying — but a brief notice with a link
 *   to the full Datenschutzerklärung is the polite middle ground that
 *   German users have come to expect.
 *
 * Persistence:
 *   localStorage key `toleranz_privacy_notice_v1`. Versioned so we can
 *   force the notice back if the data-processing situation changes
 *   (e.g. adding a CRM, A/B testing, etc.) — bump to `_v2`.
 */
const STORAGE_KEY = 'toleranz_privacy_notice_v1';

export default function PrivacyNotice() {
  const [dismissed, setDismissed] = useState(() => {
    if (typeof localStorage === 'undefined') return true;
    return localStorage.getItem(STORAGE_KEY) === 'true';
  });

  if (dismissed) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, 'true'); } catch { /* private mode */ }
    setDismissed(true);
  };

  return (
    <div
      role="region"
      aria-label="Datenschutz-Hinweis"
      className="fixed inset-x-3 bottom-3 md:left-auto md:right-4 md:bottom-4 md:max-w-md z-[150] bg-page text-ink rounded-2xl shadow-2xl border border-line animate-in slide-in-from-bottom-4 fade-in duration-300"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="p-2 bg-[color:var(--color-ok)]/20 rounded-xl shrink-0">
          <ShieldCheck size={18} className="text-ok" />
        </div>
        <div className="flex-1 min-w-0 text-xs leading-relaxed text-ink-2">
          <p className="font-bold text-ink mb-1">Datenschutz auf einen Blick</p>
          <p>
            Wir nutzen <span className="font-bold text-ink">keine Tracking-Cookies</span>.
            Analyse via Plausible erfolgt anonym und cookie-frei.
            Notwendig sind nur Cookies für deinen Login und für Stripe-Zahlungen.{' '}
            <Link
              to="/legal/privacy"
              className="text-ok hover:text-emerald-200 underline underline-offset-2"
            >
              Mehr in der Datenschutzerklärung
            </Link>.
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Hinweis schließen"
          className="p-1.5 text-ink-3 hover:text-ink hover:bg-surface rounded-lg transition-all shrink-0"
        >
          <X size={16} />
        </button>
      </div>
      {/* "Verstanden" button: emerald-700 base (not emerald-600) so
          white text clears WCAG AA 4.5:1 contrast. emerald-600 alone
          gives ~3.65 which fails. Hover stays at emerald-600 for the
          interactive feedback. */}
      <button
        type="button"
        onClick={handleDismiss}
        className="w-full px-4 py-2.5 bg-emerald-700 hover:bg-emerald-600 text-ink text-xs font-black uppercase tracking-widest rounded-b-2xl transition-colors"
      >
        Verstanden
      </button>
    </div>
  );
}
